// Shared UI Components for consistent design patterns across the app

import { Loader2, AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';
import { Card, CardContent } from './card';

// Loading skeleton for cards
export const LoadingSkeleton = ({ rows = 3, className = '' }) => (
  <div className={`animate-pulse space-y-4 ${className}`}>
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    ))}
  </div>
);

// Page loading state
export const PageLoader = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <Loader2 className="w-10 h-10 text-navy animate-spin mb-4" />
    <p className="text-gray-600">{message}</p>
  </div>
);

// Empty state component
export const EmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  action,
  actionLabel,
  className = ''
}) => (
  <div className={`text-center py-12 ${className}`}>
    {Icon && (
      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
        <Icon className="w-8 h-8 text-gray-400" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-navy mb-2">{title}</h3>
    {description && <p className="text-gray-600 mb-4 max-w-sm mx-auto">{description}</p>}
    {action && actionLabel && (
      <button
        onClick={action}
        className="inline-flex items-center px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// Success message component
export const SuccessMessage = ({ title, description, onDismiss }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="font-medium text-green-800">{title}</p>
      {description && <p className="text-sm text-green-700 mt-1">{description}</p>}
    </div>
    {onDismiss && (
      <button onClick={onDismiss} className="text-green-600 hover:text-green-800">
        <XCircle className="w-5 h-5" />
      </button>
    )}
  </div>
);

// Error message component
export const ErrorMessage = ({ title, description, onRetry, onDismiss }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
    <AlertCircle className="w-5 h-5 text-red flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="font-medium text-red">{title}</p>
      {description && <p className="text-sm text-red/80 mt-1">{description}</p>}
      {onRetry && (
        <button 
          onClick={onRetry}
          className="text-sm text-red underline mt-2 hover:no-underline"
        >
          Try again
        </button>
      )}
    </div>
    {onDismiss && (
      <button onClick={onDismiss} className="text-red hover:text-red/80">
        <XCircle className="w-5 h-5" />
      </button>
    )}
  </div>
);

// Info banner component
export const InfoBanner = ({ title, description, className = '' }) => (
  <div className={`bg-light-blue/10 border border-light-blue/20 rounded-lg p-4 flex items-start space-x-3 ${className}`}>
    <Info className="w-5 h-5 text-light-blue flex-shrink-0 mt-0.5" />
    <div>
      <p className="font-medium text-navy">{title}</p>
      {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
    </div>
  </div>
);

// Warning banner component
export const WarningBanner = ({ title, description, action, actionLabel }) => (
  <div className="bg-gold/20 border border-gold/40 rounded-lg p-4 flex items-start space-x-3">
    <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="font-medium text-navy">{title}</p>
      {description && <p className="text-sm text-gray-700 mt-1">{description}</p>}
    </div>
    {action && actionLabel && (
      <button 
        onClick={action}
        className="text-sm font-medium text-navy bg-gold/30 px-3 py-1 rounded hover:bg-gold/50 transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// Stat card component
export const StatCard = ({ 
  icon: Icon, 
  label, 
  value, 
  trend,
  trendUp = true,
  colorClass = 'text-navy',
  bgClass = 'bg-navy/10',
  onClick,
  className = ''
}) => (
  <Card 
    className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
    onClick={onClick}
    data-testid={`stat-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className={`text-2xl sm:text-3xl font-bold ${colorClass}`}>{value}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trendUp ? 'text-green-600' : 'text-red'}`}>
              {trend}
            </p>
          )}
        </div>
        {Icon && (
          <div className={`w-11 h-11 ${bgClass} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${colorClass}`} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Page header component
export const PageHeader = ({ 
  title, 
  subtitle, 
  backLink,
  actions,
  className = ''
}) => (
  <div className={`mb-6 ${className}`}>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-navy" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {title}
        </h1>
        {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center space-x-2">{actions}</div>}
    </div>
  </div>
);

// Status badge with consistent colors
export const StatusBadge = ({ status, size = 'default' }) => {
  const statusConfig = {
    // Registration statuses
    'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
    'approved': { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
    'registered': { bg: 'bg-green-100', text: 'text-green-800', label: 'Registered' },
    'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
    'incomplete': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Incomplete' },
    
    // Eligibility statuses
    'eligible': { bg: 'bg-green-100', text: 'text-green-800', label: 'Eligible' },
    'cleared': { bg: 'bg-green-100', text: 'text-green-800', label: 'Cleared' },
    'pending_docs': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Docs' },
    'pending_payment': { bg: 'bg-red-100', text: 'text-red-800', label: 'Pending Payment' },
    'pending_verification': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pending Verification' },
    'waitlisted': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Waitlisted' },
    'not_eligible': { bg: 'bg-red-100', text: 'text-red-800', label: 'Not Eligible' },
    
    // Event statuses
    'scheduled': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled' },
    'canceled': { bg: 'bg-red-100', text: 'text-red-800', label: 'Canceled' },
    'completed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Completed' },
    'postponed': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Postponed' },
    
    // RSVP statuses
    'attending': { bg: 'bg-green-100', text: 'text-green-800', label: 'Attending' },
    'absent': { bg: 'bg-red-100', text: 'text-red-800', label: 'Absent' },
    'late': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Late' },
    'no_response': { bg: 'bg-gray-100', text: 'text-gray-600', label: 'No Response' },
    
    // Payment statuses
    'paid': { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
    'overdue': { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
    'partial': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Partial' },
    
    // Volunteer statuses
    'open': { bg: 'bg-green-100', text: 'text-green-800', label: 'Open' },
    'full': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Full' },
    'closed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' },
    'confirmed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmed' },
    
    // Post statuses
    'published': { bg: 'bg-green-100', text: 'text-green-800', label: 'Published' },
    'pending_approval': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Approval' },
    'archived': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Archived' },
    
    // Default
    'default': { bg: 'bg-gray-100', text: 'text-gray-700', label: status }
  };
  
  const config = statusConfig[status?.toLowerCase()] || statusConfig.default;
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1';
  
  return (
    <span className={`inline-flex items-center ${sizeClasses} rounded-full font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Progress bar component
export const ProgressBar = ({ 
  value, 
  max = 100, 
  label,
  showPercentage = true,
  colorClass = 'bg-navy',
  className = ''
}) => {
  const percentage = Math.min(Math.round((value / max) * 100), 100);
  
  return (
    <div className={className}>
      {(label || showPercentage) && (
        <div className="flex items-center justify-between mb-1">
          {label && <span className="text-sm text-gray-600">{label}</span>}
          {showPercentage && <span className="text-sm font-medium text-navy">{percentage}%</span>}
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className={`h-2.5 rounded-full transition-all duration-500 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Action card for quick actions
export const ActionCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  badge,
  badgeColor = 'bg-red',
  className = ''
}) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-navy/30 hover:shadow-sm transition-all ${className}`}
  >
    <div className="flex items-center space-x-3">
      {Icon && (
        <div className="w-10 h-10 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0 relative">
          <Icon className="w-5 h-5 text-navy" />
          {badge && (
            <span className={`absolute -top-1 -right-1 w-5 h-5 ${badgeColor} text-white text-xs rounded-full flex items-center justify-center font-medium`}>
              {badge}
            </span>
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-navy truncate">{title}</p>
        {description && <p className="text-sm text-gray-500 truncate">{description}</p>}
      </div>
    </div>
  </button>
);
